import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@web/components/Button';
import { useSession } from '@web/hooks/useSession';
import { signInWithPassword } from '@web/lib/auth';
import { keys } from '@web/lib/keys';

/**
 * Sign in.
 *
 * There is no self-service signup anywhere in this product — every account is
 * created by an administrator — so this screen has exactly two fields and no
 * links. The error copy is deliberately vague about which half was wrong.
 */
export function LoginPage() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const from = new URLSearchParams(location.search).get('from') ?? '/board';

  if (session.data != null) {
    void navigate(from, { replace: true });
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);

    const result = await signInWithPassword(email, password);
    setPending(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }
    await client.invalidateQueries({ queryKey: keys.me });
    void navigate(from, { replace: true });
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-ground px-4">
      <form
        onSubmit={(event) => {
          void submit(event);
        }}
        className="w-full max-w-sm border border-rule bg-panel"
      >
        <div className="border-b border-rule px-4 py-3">
          <div className="font-mono text-[10px] tracking-[0.14em] text-ink-faint uppercase">
            ADEL
          </div>
          <h1 className="mt-1 text-sm font-semibold">Operations console</h1>
        </div>

        <div className="flex flex-col gap-3 px-4 py-4">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
              Email
            </span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              className="h-11 border border-rule-strong bg-ground px-2"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
              Password
            </span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              className="h-11 border border-rule-strong bg-ground px-2"
            />
          </label>

          {error !== null && (
            <p role="alert" className="border-l-2 border-signal pl-2 text-xs text-signal">
              {error}
            </p>
          )}

          <Button type="submit" tone="primary" size="touch" disabled={pending} className="mt-1">
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-[11px] text-ink-faint">
            Accounts are created by an administrator. Ask one if you cannot get in.
          </p>
        </div>
      </form>
    </div>
  );
}
